import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Target, ClipboardList, Wrench, FileBarChart2, Printer, Users, BookOpen, History, Settings, Bell, LogOut, Menu, Network, KeyRound } from "lucide-react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { useGlobalYear } from "@/lib/useGlobalYear";
import { YEARS } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays } from "lucide-react";
import { useAuth, isMgmt } from "@/lib/auth";
import { api } from "@/lib/api";
import { roleLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { GlobalSearch } from "@/components/GlobalSearch";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { LangToggle } from "@/components/LangToggle";
import { Oreo } from "@/components/Oreo";

const GROUPS = [
  { title: "nav_utama", items: [{ to: "/dashboard", label: "dashboard", icon: LayoutDashboard }, { to: "/organisasi", label: "organisasi", icon: Network }] },
  { title: "nav_monitoring", items: [
    { to: "/kpi", label: "kpi", icon: Target },
    { to: "/realisasi", label: "realisasi", icon: ClipboardList },
  ] },
  { title: "nav_laporan", items: [
    { to: "/improvement", label: "improvement", icon: Wrench },
    { to: "/laporan", label: "laporan", icon: FileBarChart2 },
    { to: "/matrix", label: "matrix", icon: Printer },
  ] },
  { title: "nav_admin", items: [
    { to: "/master", label: "master", icon: Users, roles: ["admin", "top", "coo", "manager"] },
    { to: "/panduan", label: "panduan", icon: BookOpen },
    { to: "/audit", label: "audit", icon: History },
    { to: "/pengaturan", label: "pengaturan", icon: Settings, roles: ["admin", "top", "coo", "manager"] },
  ] },
];

function Sidebar({ user, open }) {
  const { t } = useLang();
  return (
    <aside data-testid="sidebar" className={cn("no-print fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col transition-transform duration-200 lg:translate-x-0", open ? "translate-x-0" : "-translate-x-full")}>
      <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-200">
        <div className="w-8 h-8 rounded bg-slate-900 text-white font-heading font-bold flex items-center justify-center text-sm">Y</div>
        <div>
          <div className="font-heading font-bold text-slate-900 leading-tight" data-testid="app-name">Y-ナビ / Y-Navi</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Yutaka Navigation</div>
        </div>
      </div>
      <div className="mx-3 mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2" data-testid="sidebar-oreo"><Oreo mood="semangat" size={40} message={t("oreo_hi")} /></div>
      <nav className="flex-1 overflow-y-auto px-3 py-3">
        {GROUPS.map((g, i) => {
          const items = g.items.filter((it) => !it.roles || it.roles.includes(user.role));
          if (!items.length) return null;
          return (
            <div key={g.title} className={cn(i > 0 && "mt-3 pt-3 border-t border-slate-200")}>
              <div className="label-caps px-3 mb-1.5">{t(g.title)}</div>
              {items.map((it) => (
                <NavLink key={it.to} to={it.to} data-testid={`nav-${it.to.slice(1)}`} className={({ isActive }) => cn("nav-item", isActive && "active")}>
                  <it.icon size={16} /> {t(it.label)}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="p-4 border-t border-slate-200 text-xs text-slate-500">Y-Navi v1.0 · Bahasa Indonesia</div>
    </aside>
  );
}

function NotifBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const seen = useRef(null);
  const load = () => api.get("/notifications").then((r) => {
    const fresh = seen.current ? r.data.items.filter((n) => !n.read && !seen.current.has(n.id)) : [];
    fresh.slice(0, 3).forEach((n) => toast(n.title, { description: n.message, action: n.link ? { label: "Buka", onClick: () => nav(n.link) } : undefined }));
    seen.current = new Set(r.data.items.map((n) => n.id));
    setData(r.data);
  }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []); // eslint-disable-line
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" className="relative p-2 rounded-md hover:bg-slate-100 transition-colors">
          <Bell size={18} />
          {data.unread > 0 && <span data-testid="notif-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-2 border-b">
          <span className="font-heading font-semibold text-sm">Notifikasi</span>
          <button data-testid="notif-read-all" className="text-xs text-blue-600 hover:underline" onClick={() => api.post("/notifications/read-all").then(load)}>Tandai semua dibaca</button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <div className="p-4 text-sm text-slate-500">Belum ada notifikasi</div>}
          {data.items.map((n) => (
            <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => { api.post(`/notifications/${n.id}/read`).then(load); if (n.link) nav(n.link); }}
              className={cn("w-full text-left px-4 py-2.5 border-b last:border-0 hover:bg-slate-50", !n.read && "bg-blue-50/60")}>
              <div className="text-sm font-medium text-slate-900">{n.title}</div>
              <div className="text-xs text-slate-600 line-clamp-2">{n.message}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.at).toLocaleString("id-ID")}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [cp, setCp] = useState(false);
  const [year, setYear] = useGlobalYear();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-background">
      <Sidebar user={user} open={open} />
      {open && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:pl-64">
        <header className="no-print sticky top-0 z-40 h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="sidebar-toggle"><Menu size={18} /></button>
            <span className="label-caps hidden md:inline">{isMgmt(user) ? t("mode_mgmt") : t("mode_pelaksana")}</span>
            <GlobalSearch />
            <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 pl-2.5 pr-1 h-9" data-testid="header-year-picker" title="Tahun aktif untuk seluruh aplikasi">
              <CalendarDays size={14} className="text-slate-500" /><span className="label-caps hidden sm:inline">{t("tahun")}</span>
              <Select value={year} onValueChange={setYear}><SelectTrigger data-testid="header-year-select" className="h-7 w-[84px] border-0 bg-transparent shadow-none font-heading font-bold text-slate-900 focus:ring-0"><SelectValue /></SelectTrigger>
                <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LangToggle className="hidden sm:inline-flex" />
            <NotifBell />
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span data-testid="header-user-name" className="text-sm font-medium text-slate-900">{user.name}</span>
              <span data-testid="header-user-role" className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">{roleLabel(user)}</span>
            </div>
            <Button data-testid="change-password-btn" variant="ghost" size="icon" title={t("ganti_password")} onClick={() => setCp(true)}><KeyRound size={16} /></Button>
            <Button data-testid="logout-btn" variant="ghost" size="icon" title={t("keluar")} onClick={async () => { await logout(); nav("/login"); }}><LogOut size={16} /></Button>
            {cp && <ChangePasswordDialog onClose={() => setCp(false)} />}
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px]"><Outlet /></main>
      </div>
    </div>
  );
}
